/* NOVA Pro — extended components */

// Image slider
class ImageSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-slider-track]');
    this.slides = this.querySelectorAll('[data-slider-slide]');
    this.index = 0;
    this.total = this.slides.length;
    if (this.total < 2) return;
    this.querySelector('[data-slider-prev]')?.addEventListener('click', () => this.go(this.index - 1));
    this.querySelector('[data-slider-next]')?.addEventListener('click', () => this.go(this.index + 1));
    if (this.dataset.autoplay === 'true') {
      this._timer = setInterval(() => this.go(this.index + 1), 5000);
    }
  }
  go(i) {
    this.index = (i + this.total) % this.total;
    this.track.style.transform = `translateX(-${this.index * 100}%)`;
  }
  disconnectedCallback() { clearInterval(this._timer); }
}
customElements.define('image-slider', ImageSlider);

// Vertical ticker
class VerticalTicker extends HTMLElement {
  connectedCallback() {
    const track = this.querySelector('[data-vt-track]');
    if (track) track.innerHTML = track.innerHTML + track.innerHTML;
    const speed = parseInt(this.dataset.speed, 10) || 16;
    this.style.setProperty('--vt-speed', `${speed}s`);
  }
}
customElements.define('vertical-ticker', VerticalTicker);

// Comparison slider
class ComparisonSlider extends HTMLElement {
  connectedCallback() {
    this.after = this.querySelector('[data-cs-after]');
    this.handle = this.querySelector('[data-cs-handle]');
    let dragging = false;
    const set = (pct) => {
      const p = Math.max(5, Math.min(95, pct));
      this.after.style.width = `${p}%`;
      this.handle.style.left = `${p}%`;
    };
    const move = (clientX) => {
      const rect = this.getBoundingClientRect();
      set(((clientX - rect.left) / rect.width) * 100);
    };
    this.handle?.addEventListener('mousedown', () => { dragging = true; });
    window.addEventListener('mouseup', () => { dragging = false; });
    window.addEventListener('mousemove', (e) => { if (dragging) move(e.clientX); });
    this.addEventListener('touchmove', (e) => move(e.touches[0].clientX), { passive: true });
  }
}
customElements.define('comparison-slider', ComparisonSlider);

// Shoppable image pins
class ShoppableImage extends HTMLElement {
  connectedCallback() {
    this.cards = this.querySelectorAll('[data-pin-card]');
    this.cards.forEach((card) => {
      card.addEventListener('click', (e) => e.stopPropagation());
    });
    this.querySelectorAll('[data-pin]').forEach((pin) => {
      pin.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = pin.dataset.pin;
        this.cards.forEach((c) => { c.hidden = c.dataset.pinCard !== id; });
      });
    });
    this._onDocClick = () => {
      this.cards.forEach((c) => { c.hidden = true; });
    };
    document.addEventListener('click', this._onDocClick);
  }
  disconnectedCallback() {
    document.removeEventListener('click', this._onDocClick);
  }
}
customElements.define('shoppable-image', ShoppableImage);

// Bundle deals selector
class BundleDeals extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('[data-bundle-deal]').forEach((deal) => {
      deal.addEventListener('click', () => {
        this.querySelectorAll('[data-bundle-deal]').forEach((d) => d.dataset.selected = 'false');
        deal.dataset.selected = 'true';
        const qty = deal.dataset.qty;
        const input = document.querySelector('product-form form input[name="quantity"]')
          || document.querySelector('#quantity');
        if (input && qty) {
          input.value = qty;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });
    // Auto-priced deals track the live variant price
    this._onVariant = (e) => this.reprice(e.detail?.variant);
    document.addEventListener('variant:change', this._onVariant);
  }
  disconnectedCallback() { document.removeEventListener('variant:change', this._onVariant); }
  reprice(variant) {
    if (!variant || typeof formatMoney !== 'function') return;
    const compareUnit = Math.max(variant.compare_at_price || 0, variant.price);
    this.querySelectorAll('[data-bundle-deal][data-auto-price]').forEach((deal) => {
      const qty = parseInt(deal.dataset.qty, 10) || 1;
      const pct = parseInt(deal.dataset.pct, 10) || 0;
      const total = variant.price * qty;
      const price = pct > 0 ? Math.round(total * (100 - pct) / 100) : total;
      const compare = Math.max(compareUnit * qty, price);
      const priceEl = deal.querySelector('[data-deal-price]');
      if (priceEl) priceEl.textContent = formatMoney(price);
      const compareEl = deal.querySelector('[data-deal-compare]');
      if (compareEl) {
        compareEl.hidden = !(compare > price);
        if (compare > price) compareEl.textContent = formatMoney(compare);
      }
    });
  }
}
customElements.define('bundle-deals', BundleDeals);

// Scroll progress
class ScrollProgress extends HTMLElement {
  connectedCallback() {
    this.bar = this.querySelector('[data-scroll-bar]');
    this._onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      const pct = h > 0 ? (window.scrollY / h) * 100 : 0;
      if (this.bar) this.bar.style.width = `${pct}%`;
    };
    window.addEventListener('scroll', this._onScroll, { passive: true });
    this._onScroll();
  }
  disconnectedCallback() { window.removeEventListener('scroll', this._onScroll); }
}
customElements.define('scroll-progress', ScrollProgress);

// Music player toggle
class MusicPlayer extends HTMLElement {
  connectedCallback() {
    this.audio = this.querySelector('audio');
    this.btn = this.querySelector('[data-music-toggle]');
    this.btn?.addEventListener('click', () => {
      if (!this.audio) return;
      if (this.audio.paused) { this.audio.play(); this.btn.textContent = '⏸'; }
      else { this.audio.pause(); this.btn.textContent = '♪'; }
    });
  }
}
customElements.define('music-player', MusicPlayer);

// Parallax hero
class ParallaxHero extends HTMLElement {
  connectedCallback() {
    this.bg = this.querySelector('[data-parallax-bg]');
    if (!this.bg) return;
    this._onScroll = () => {
      const rect = this.getBoundingClientRect();
      const offset = Math.max(0, -rect.top * 0.3);
      this.bg.style.transform = `translateY(${offset}px)`;
    };
    window.addEventListener('scroll', this._onScroll, { passive: true });
  }
  disconnectedCallback() { window.removeEventListener('scroll', this._onScroll); }
}
customElements.define('parallax-hero', ParallaxHero);

// Predictive search
class PredictiveSearchForm extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-predictive-results]');
    if (!this.input || !this.results) return;
    let timer;
    this.input.addEventListener('input', () => {
      clearTimeout(timer);
      const q = this.input.value.trim();
      if (q.length < 2) { this.results.hidden = true; return; }
      timer = setTimeout(() => this.search(q), 250);
    });
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) this.results.hidden = true;
    });
  }
  async search(q) {
    const res = await fetch(`${window.Shopify?.routes?.predictive_search_url || '/search/suggest'}?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&section_id=predictive-search`);
    const text = await res.text();
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const html = doc.querySelector('[data-predictive-results]')?.innerHTML;
    if (html) { this.results.innerHTML = html; this.results.hidden = false; }
  }
}
customElements.define('predictive-search-form', PredictiveSearchForm);

// Mobile drawer
class MobileDrawer extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('[data-drawer-close]').forEach((el) => el.addEventListener('click', () => this.close()));
    document.querySelectorAll('[data-drawer-open]').forEach((btn) => btn.addEventListener('click', () => this.open()));
  }
  open() { this.dataset.open = 'true'; document.body.style.overflow = 'hidden'; }
  close() { this.dataset.open = 'false'; document.body.style.overflow = ''; }
}
customElements.define('mobile-drawer', MobileDrawer);

// Cart upsell add
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('[data-upsell-add]');
  if (!btn) return;
  e.preventDefault();
  const id = btn.dataset.variantId;
  if (!id) return;
  const addResponse = await fetch(novaRoute('cartAdd', '/cart/add.js'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id, quantity: 1 }) });
  if (!addResponse.ok) return;
  const cart = await (await fetch(novaRoute('cart', '/cart') + '.js')).json();
  if (typeof updateCartCount === 'function') updateCartCount(cart.item_count);
  await window.NovaCart?.checkGift?.(cart);
  const drawer = document.querySelector('cart-drawer');
  if (drawer?.refresh) { await drawer.refresh(); drawer.open(); }
});

// Track order (redirect to carrier lookup placeholder)
document.addEventListener('submit', (e) => {
  const form = e.target.closest('[data-track-order]');
  if (!form) return;
  e.preventDefault();
  const num = form.querySelector('[name="order_number"]')?.value;
  const email = form.querySelector('[name="email"]')?.value;
  if (num && email) window.location.href = `/account/login?email=${encodeURIComponent(email)}`;
});
// ---------- Shoppable video reels (mobile-only, TikTok-style vertical feed) ----------
class VideoReels extends HTMLElement {
  connectedCallback() {
    this.feed = this.querySelector('[data-reels-feed]');
    this.scroller = this.querySelector('[data-reels-scroller]');
    this.items = Array.from(this.querySelectorAll('[data-reels-item]'));
    this.soundOn = false;
    if (!this.feed || !this.items.length) return;

    this.querySelectorAll('[data-reels-tab]').forEach((tab) =>
      tab.addEventListener('click', () => {
        if (tab.dataset.reelsTab === 'reels') this.open();
        else this.close();
      })
    );

    // Play the (mostly) visible video, pause the rest — the TikTok loop
    this._observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target.querySelector('video');
        if (!video) return;
        if (entry.isIntersecting) {
          video.currentTime = 0;
          video.muted = !this.soundOn;
          video.play().catch(() => {
            // Autoplay with sound blocked — degrade to muted for this video
            video.muted = true;
            this.setMuteUI(entry.target, true);
            video.play().catch(() => {});
          });
        } else {
          video.pause();
        }
      });
    }, { root: this.scroller, threshold: 0.6 });

    this.feed.addEventListener('click', (e) => {
      const mute = e.target.closest('[data-reels-mute]');
      if (mute) {
        this.soundOn = !this.soundOn;
        this.applySound();
        return;
      }
      const video = e.target.closest('video');
      if (video) video.paused ? video.play().catch(() => {}) : video.pause();
    });

    // Browser/phone back gesture closes the feed
    this._onPop = () => { if (!this.feed.hidden) this.close(false); };
    window.addEventListener('popstate', this._onPop);
    if (window.location.hash === '#reels') this.open(false);
  }
  disconnectedCallback() {
    this._observer?.disconnect();
    window.removeEventListener('popstate', this._onPop);
  }

  setActiveTabs(mode) {
    this.querySelectorAll('[data-reels-tab]').forEach((tab) => {
      const active = tab.dataset.reelsTab === mode;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active);
    });
  }
  setMuteUI(item, muted) {
    const btn = item.querySelector('[data-reels-mute]');
    if (btn) btn.dataset.muted = String(muted);
  }
  applySound() {
    this.items.forEach((item) => {
      const video = item.querySelector('video');
      if (video) video.muted = !this.soundOn;
      this.setMuteUI(item, !this.soundOn);
    });
  }

  open(pushHash = true) {
    this.feed.hidden = false;
    document.body.classList.add('reels-open');
    document.body.style.overflow = 'hidden';
    this.setActiveTabs('reels');
    // Opened by a tap = user gesture, so sound is allowed
    this.soundOn = true;
    this.applySound();
    this.scroller.scrollTop = 0;
    this.items.forEach((item) => this._observer.observe(item));
    if (pushHash && window.location.hash !== '#reels') {
      history.pushState({ reels: true }, '', '#reels');
    }
  }
  close(updateHistory = true) {
    this.items.forEach((item) => {
      this._observer.unobserve(item);
      item.querySelector('video')?.pause();
    });
    this.feed.hidden = true;
    document.body.classList.remove('reels-open');
    document.body.style.overflow = '';
    this.setActiveTabs('home');
    if (updateHistory && window.location.hash === '#reels') {
      history.replaceState({}, '', window.location.pathname + window.location.search);
    }
  }
}
customElements.define('video-reels', VideoReels);

document.addEventListener('click', (event) => {
  const unitButton = event.target.closest('[data-size-unit]');
  if (!unitButton) return;
  const popup = unitButton.closest('.size-guide-popup');
  if (!popup) return;
  const unit = unitButton.dataset.sizeUnit;
  popup.querySelectorAll('[data-size-unit]').forEach((button) => {
    button.dataset.active = String(button === unitButton);
  });
  popup.querySelectorAll('[data-size-value]').forEach((value) => {
    value.hidden = value.dataset.sizeValue !== unit;
  });
});
